import type { Connection } from "@riea-water/contracts";
import { useState } from "react";
import { useOutletContext } from "react-router";
import { useConnections, useSyncs, useTriggerSync } from "../api/queries.js";
import type { ShellContext } from "../shell/Shell.js";
import { useToast } from "../shell/toast.js";
import {
  columnsOf,
  CORE_CAPABILITIES,
  DIRECTION,
  healthPill,
  lastActivity,
  summaryCounts,
} from "./connections-helpers.js";

/** Setup · Connections (CON-01 to CON-03, CON-06): the integration map as the prototype draws it. */
export function Connections() {
  const { me, modules, client } = useOutletContext<ShellContext>();
  const connections = useConnections(client);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { toast, Toast } = useToast();
  const trigger = useTriggerSync(client);

  const items = connections.data?.items ?? [];
  const selected = items.find((c) => c.id === selectedId) ?? items[0] ?? null;
  const syncs = useSyncs(client, selected?.id ?? null);
  const counts = summaryCounts(items);
  const columns = columnsOf(items);
  const canSync = me.role === "tenant_admin";

  const card = (c: Connection) => {
    const pill = healthPill(c.health);
    return (
      <button
        key={c.id}
        type="button"
        className="conn"
        aria-pressed={selected?.id === c.id}
        onClick={() => setSelectedId(c.id)}
      >
        <span className="nm">{c.name}</span>
        <span className="dir">{DIRECTION[c.direction]}</span>
        <span className="dt2">{c.data}</span>
        <span className="dt2">
          <span className={`pill ${pill.tone}`}>{pill.label}</span>{" "}
          <span className="mono faint" style={{ fontSize: ".68rem" }}>
            {c.frequency}
          </span>
        </span>
      </button>
    );
  };

  const onSync = () => {
    if (!selected) return;
    trigger.mutate(selected.id, {
      onSuccess: (sync) =>
        toast(
          `Sync of ${selected.name} ${sync.status === "running" ? "started" : sync.status} · ConnectionSyncStarted recorded`,
        ),
      onError: (error) => toast(`Sync refused · ${error.message}`),
    });
  };

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Setup · Integration map</div>
          <h1>Connections</h1>
        </div>
        <div className="split" aria-label="Connection health summary">
          <span className="pill ok">{counts.connected} connected</span>
          <span className="pill info">{counts.simulated} simulated</span>
          {counts.attention > 0 ? (
            <span className="pill warn">{counts.attention} attention</span>
          ) : null}
          <span className="pill off">{counts.planned} planned</span>
        </div>
      </div>
      <p className="muted" style={{ margin: 0, maxWidth: "78ch" }}>
        Everything the platform needs to work properly, and the direction data flows. RIEA Pro reads
        from the utility&apos;s own systems, keeps the evidenced record, and sends only gated data
        to the market through the utility&apos;s existing market gateway.
      </p>

      {connections.isPending ? (
        <section className="card">
          <div className="body">
            <div className="skeleton" />
            <div className="skeleton" style={{ width: "60%", marginTop: 8 }} />
          </div>
        </section>
      ) : connections.isError ? (
        <section className="card">
          <div className="empty">
            The connections could not be loaded: {connections.error.message}.
          </div>
        </section>
      ) : (
        <div className="cmap">
          <div className="ccol">
            <div className="eyebrow">{me.tenantName} systems</div>
            {columns.utility.map(card)}
          </div>
          <div className="ccol">
            <div className="core">
              <div className="eyebrow">RIEA Pro · UK-hosted</div>
              <h3>Water Market platform</h3>
              {CORE_CAPABILITIES.map((name) => (
                <div className="mod" key={name}>
                  <span>{name}</span>
                  <span className="pill info">core</span>
                </div>
              ))}
              {modules.items
                .filter((m) => m.tenantState === "active" || m.tenantState === "preview")
                .map((m) => (
                  <div className="mod" key={m.key}>
                    <span>{m.name}</span>
                    <span className={`pill ${m.tenantState === "preview" ? "vio" : "ok"}`}>
                      {m.tenantState}
                    </span>
                  </div>
                ))}
              <div style={{ marginTop: 6 }} className="eyebrow">
                Selected connection
              </div>
              {selected ? (
                <div
                  style={{
                    border: "1px solid var(--line2)",
                    borderRadius: 10,
                    padding: 12,
                    background: "var(--panel)",
                    display: "grid",
                    gap: 6,
                  }}
                  aria-live="polite"
                >
                  <b>{selected.name}</b>
                  <span className="split">
                    <span className={`pill ${healthPill(selected.health).tone}`}>
                      {healthPill(selected.health).label}
                    </span>
                    <span className="dir">{DIRECTION[selected.direction]}</span>
                  </span>
                  <div className="kv">
                    <span className="k">Data</span>
                    <span className="v">{selected.data}</span>
                    <span />
                  </div>
                  <div className="kv">
                    <span className="k">Channel</span>
                    <span className="v">{selected.channel}</span>
                    <span />
                  </div>
                  <div className="kv">
                    <span className="k">Frequency</span>
                    <span className="v">{selected.frequency}</span>
                    <span />
                  </div>
                  <div className="kv">
                    <span className="k">Last activity</span>
                    <span className="v">{lastActivity(selected.lastActivityAt)}</span>
                    <span />
                  </div>
                  {selected.lastError ? (
                    <div className="kv">
                      <span className="k">Last error</span>
                      <span className="v" style={{ color: "var(--crit)" }}>
                        {selected.lastError}
                      </span>
                      <span />
                    </div>
                  ) : null}
                  <p className="muted" style={{ fontSize: ".8rem", margin: "4px 0 0" }}>
                    {selected.why}
                  </p>
                  {canSync &&
                  selected.templates.length > 0 &&
                  selected.buildStatus !== "planned" ? (
                    <div className="split" style={{ marginTop: 4 }}>
                      <button
                        className="btn sm"
                        type="button"
                        onClick={onSync}
                        disabled={trigger.isPending}
                      >
                        Sync now
                      </button>
                      <span className="faint" style={{ fontSize: ".74rem" }}>
                        pulls the drop directory
                      </span>
                    </div>
                  ) : null}
                  {syncs.data && syncs.data.length > 0 ? (
                    <details>
                      <summary className="muted" style={{ fontSize: ".78rem", cursor: "pointer" }}>
                        Last {syncs.data.length} syncs
                      </summary>
                      <div className="tablewrap">
                        <table>
                          <tbody>
                            {syncs.data.map((s) => (
                              <tr key={s.id}>
                                <td className="mono" style={{ fontSize: ".72rem" }}>
                                  {lastActivity(s.startedAt)}
                                </td>
                                <td>
                                  <span
                                    className={`pill ${s.status === "completed" ? "ok" : s.status === "failed" ? "crit" : "info"}`}
                                  >
                                    {s.status}
                                  </span>
                                </td>
                                <td className="mono faint" style={{ fontSize: ".7rem" }}>
                                  {s.trigger}
                                  {s.counts["read"] !== undefined
                                    ? ` · ${s.counts["read"]} read`
                                    : ""}
                                  {s.counts["quarantined"]
                                    ? ` · ${s.counts["quarantined"]} quarantined`
                                    : ""}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          <div className="ccol">
            <div className="eyebrow">Central market · via market gateway</div>
            {columns.market.map(card)}
            <div className="eyebrow" style={{ marginTop: 12 }}>
              Reference and RIEA Pro
            </div>
            {columns.referenceAndPlatform.map(card)}
          </div>
        </div>
      )}

      <section className="card">
        <header>
          <h2>Not connected, by design</h2>
          <span className="muted" style={{ fontSize: ".78rem" }}>
            keeps the platform in its lane
          </span>
        </header>
        <div className="tablewrap">
          <table>
            <tbody>
              {(connections.data?.notConnected ?? []).map((n) => (
                <tr key={n.name}>
                  <td>
                    <span className="pill off">none</span>
                  </td>
                  <td>
                    <b>{n.name}</b>
                  </td>
                  <td className="muted">{n.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <Toast />
    </>
  );
}
